import { useState } from "react";
import { Link } from "react-router-dom";
import { GraduationCap, Users, Building2, MapPin, ArrowRight, CheckCircle2, Clock, Leaf, Target, Lightbulb, ClipboardList, Sparkles, BookOpen, School, Landmark, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";

const CATEGORIES = [
  {
    id: "education",
    num: "01",
    title: "Acteurs de l'éducation",
    subtitle: "Enseignements",
    icon: School,
    color: "#C65D47",
    gradient: "from-[#C65D47] to-[#A84A38]",
    description: "Écoles primaires, collèges, lycées, universités, enseignements supérieurs et enseignement professionnel et technique.",
    detail: "Les établissements scolaires ont un rôle primordial à jouer car il est important d'apporter des connaissances solides et actualisées sur les sujets environnementaux. Former les étudiants et les professionnels à la durabilité, c'est donner les clés pour relever les défis environnementaux.",
    passculture: true,
    workshops: [
      { name: "La bataille de l'IA", duration: "2h", participants: "15 à 25 élèves" },
      { name: "Mission circulaire : le voyage d'un jouet", duration: "1h30", participants: "Classe entière (8-10 ans)" },
      { name: "La bataille de la tech", duration: "2h", participants: "15 à 25 élèves" },
      { name: "Inventons nos vies bas carbone", duration: "3h", participants: "Classe entière" },
      { name: "La fresque du climat", duration: "2h", participants: "5-14 participants" },
      { name: "La fresque de l'économie circulaire", duration: "2h", participants: "5-14 participants" },
      { name: "La fresque de la biodiversité", duration: "2h30", participants: "5-14 participants" },
      { name: "Atelier 2 tonnes", duration: "3h", participants: "6-15 participants" },
      { name: "Adaptation au Changement Climatique", duration: "1h30", participants: "À partir de 6" },
      { name: "Fresque des Frontières planétaires", duration: "1h30", participants: "À partir de 6" },
      { name: "Fresque de la Mobilité", duration: "2h", participants: "À partir de 5" },
      { name: "Atelier Pois Chiches Party", duration: "2h", participants: "À partir de 6" },
    ],
  },
  {
    id: "economie",
    num: "02",
    title: "Acteurs de l'économie",
    subtitle: "Entreprises et Associations",
    icon: Building2,
    color: "#1D3557",
    gradient: "from-[#1D3557] to-[#2A4A6D]",
    description: "TPE, PME, ETI, Grands Groupes, Institutions & Associations.",
    detail: "Les formations pour transformer votre entreprise et agir concrètement pour un futur durable. Elles sont conçues avec des modules pratiques et interactifs pour répondre à vos besoins et allier performance économique et impact positif.",
    workshops: [
      { name: "La fresque du climat", duration: "3h", participants: "5-14 participants" },
      { name: "La fresque de l'économie circulaire", duration: "3h", participants: "À partir de 6" },
      { name: "Atelier 2 tonnes", duration: "3h", participants: "6-15 participants" },
      { name: "Inventons nos vies bas carbone", duration: "1h30", participants: "À partir de 5" },
      { name: "Adaptation au Changement Climatique", duration: "1h30", participants: "À partir de 6" },
      { name: "Fresque de la biodiversité", duration: "2h30", participants: "5-14 participants" },
      { name: "Fresque de la RSE", duration: "3h", participants: "6-15 participants" },
      { name: "Fresque de la Mobilité", duration: "2h", participants: "À partir de 5" },
    ],
  },
  {
    id: "territoire",
    num: "03",
    title: "Acteurs du territoire",
    subtitle: "Collectivités",
    icon: Landmark,
    color: "#8C9D78",
    gradient: "from-[#8C9D78] to-[#6B7C5A]",
    description: "Élus et agents territoriaux, collectivités locales.",
    detail: "Les collectivités sont au coeur du territoire et possèdent une responsabilité importante dans l'accompagnement des citoyens et des entreprises. Se former sur les enjeux environnementaux permet de comprendre les problématiques écologiques, proposer des actions concrètes et adopter une vision stratégique.",
    workshops: [
      { name: "La fresque de la biodiversité", duration: "2h30", participants: "5-14 participants" },
      { name: "Fresque de l'économie circulaire", duration: "3h", participants: "À partir de 6" },
      { name: "Inventons nos vies bas carbone", duration: "1h", participants: "À partir de 5" },
      { name: "Adaptation au Changement Climatique", duration: "1h30", participants: "À partir de 6" },
    ],
  },
];

const STEPS = [
  { num: "01", title: "CADRAGE", desc: "Définition de la cible et des objectifs. Caractérisation du changement attendu.", icon: Target },
  { num: "02", title: "PRÉPARATION", desc: "Déroulé de l'atelier, création du kit pédagogique et des supports.", icon: ClipboardList },
  { num: "03", title: "ANIMATION", desc: "Pédagogie active avec des moments de partage, d'écoute et de collaboration.", icon: Sparkles },
  { num: "04", title: "ÉVALUATION", desc: "Questionnaire de validation des messages clés. Partage des résultats.", icon: CheckCircle2 },
];

const FormationsPage = () => {
  const [activeCategory, setActiveCategory] = useState("education");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedWorkshops, setSelectedWorkshops] = useState([]);
  const [quoteForm, setQuoteForm] = useState({
    service_type: "formation",
    company: "",
    contact_name: "",
    email: "",
    phone: "",
    message: "",
    budget: ""
  });

  const activeCat = CATEGORIES.find((c) => c.id === activeCategory);

  const toggleWorkshop = (workshop) => {
    setSelectedWorkshops(prev => {
      const exists = prev.find(w => w.name === workshop.name && w.catId === activeCategory);
      if (exists) return prev.filter(w => !(w.name === workshop.name && w.catId === activeCategory));
      return [...prev, { ...workshop, catId: activeCategory, catTitle: activeCat.title }];
    });
  };

  const isWorkshopSelected = (workshop) => {
    return selectedWorkshops.some(w => w.name === workshop.name && w.catId === activeCategory);
  };

  const openQuoteWithSelection = () => {
    const selectedForCat = selectedWorkshops.filter(w => w.catId === activeCategory);
    let msg = "";
    if (selectedForCat.length > 0) {
      msg = `Demande pour ${activeCat.title} :\n${selectedForCat.map(w => `- ${w.name} (${w.duration}, ${w.participants})`).join("\n")}`;
    }
    setQuoteForm(prev => ({ ...prev, message: msg }));
    setDialogOpen(true);
  };

  const handleQuoteSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/quotes`, quoteForm);
      toast.success("Votre demande a été envoyée !");
      setDialogOpen(false);
      setQuoteForm({ service_type: "formation", company: "", contact_name: "", email: "", phone: "", message: "", budget: "" });
    } catch {
      toast.error("Erreur lors de l'envoi");
    }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="formations-page">
      {/* Hero Section */}
      <section className="relative py-20 md:py-28 overflow-hidden bg-gradient-to-br from-[#1D3557] to-[#2A4A6D]">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#8C9D78] rounded-full blur-[120px]" />
          <div className="absolute bottom-0 left-0 w-72 h-72 bg-[#C65D47] rounded-full blur-[100px]" />
        </div>
        <div className="container-custom relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 text-white/90">
                <Leaf className="w-4 h-4" />
                <span className="text-sm font-medium">Sensibilisations sur-mesure</span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight" style={{ fontFamily: "Fraunces, serif" }}>
                Embarquer dans un monde souhaitable et créer un futur{" "}
                <span className="text-[#8C9D78]">désirable et joyeux</span>
              </h1>
              <p className="text-lg text-white/70 max-w-xl leading-relaxed">
                Embarquez vos équipes dans la transition écologique avec nos ateliers ludiques,
                pédagogiques et collaboratifs. RSE, climat, biodiversité, économie circulaire...
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-[#C65D47] hover:bg-[#A84A38] text-white rounded-full px-8 h-14 text-lg font-semibold" data-testid="request-quote-btn">
                      Demander un devis
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-lg bg-white z-[100]">
                    <DialogHeader>
                      <DialogTitle className="text-2xl font-bold text-[#1D3557]" style={{ fontFamily: "Fraunces, serif" }}>
                        Demande de devis
                      </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleQuoteSubmit} className="space-y-4 mt-4 relative z-10" data-testid="quote-form">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="contact_name">Nom complet *</Label>
                          <Input id="contact_name" required value={quoteForm.contact_name} onChange={(e) => setQuoteForm({ ...quoteForm, contact_name: e.target.value })} data-testid="quote-name-input" />
                        </div>
                        <div>
                          <Label htmlFor="company">Entreprise *</Label>
                          <Input id="company" required value={quoteForm.company} onChange={(e) => setQuoteForm({ ...quoteForm, company: e.target.value })} data-testid="quote-company-input" />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="email">Email *</Label>
                          <Input id="email" type="email" required value={quoteForm.email} onChange={(e) => setQuoteForm({ ...quoteForm, email: e.target.value })} data-testid="quote-email-input" />
                        </div>
                        <div>
                          <Label htmlFor="phone">Téléphone *</Label>
                          <Input id="phone" required value={quoteForm.phone} onChange={(e) => setQuoteForm({ ...quoteForm, phone: e.target.value })} data-testid="quote-phone-input" />
                        </div>
                      </div>
                      <div className="flex items-center gap-4 p-4 bg-[#1D3557]/5 rounded-xl">
                        <img
                          src="https://customer-assets.emergentagent.com/job_ee3310b8-7809-406b-b5bc-7f55e062e19b/artifacts/u5tilcmm_%40PARLACOM-MAUD-35.jpg"
                          alt="Maud Dupupet"
                          className="w-16 h-16 rounded-full object-cover object-top flex-shrink-0"
                        />
                        <div className="text-sm">
                          <p className="font-semibold text-[#1D3557]">Maud Dupupet</p>
                          <p className="text-stone-500">maud@moreati.fr</p>
                          <p className="text-stone-500">07 51 13 10 92</p>
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="message">Votre projet *</Label>
                        <Textarea id="message" required rows={4} placeholder="Décrivez votre projet, le nombre de participants, vos objectifs..." value={quoteForm.message} onChange={(e) => setQuoteForm({ ...quoteForm, message: e.target.value })} data-testid="quote-message-input" />
                      </div>
                      <Button type="submit" disabled={submitting} className="w-full bg-[#C65D47] hover:bg-[#A84A38] rounded-full h-12" data-testid="quote-submit-btn">
                        {submitting ? "Envoi en cours..." : "Envoyer ma demande"}
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
                <Button asChild variant="outline" className="border-2 border-white/30 text-white hover:bg-white/10 rounded-full h-14 text-lg">
                  <a href="#categories">Découvrir nos ateliers</a>
                </Button>
              </div>
            </div>
            <div className="relative hidden lg:flex gap-4 justify-center">
              <div className="space-y-4">
                <img
                  src="https://static.wixstatic.com/media/ce6ce7_db8b52dd2d6c4d95b95643b716ceea27~mv2.jpg/v1/fill/w_440,h_490,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/_ludivine-antonetti-maud-047.jpg"
                  alt="Atelier formation"
                  className="rounded-2xl shadow-2xl w-56 object-cover h-64"
                />
                <img
                  src="https://static.wixstatic.com/media/ce6ce7_a78925169703482a8e4253e17ed709f8~mv2.jpeg/v1/fill/w_440,h_398,fp_0.44_0.41,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/enseignement%20moreati.jpeg"
                  alt="Enseignement"
                  className="rounded-2xl shadow-2xl w-56 object-cover h-48"
                />
              </div>
              <div className="space-y-4 mt-8">
                <img
                  src="https://static.wixstatic.com/media/ce6ce7_4dd1c01e7ddb4d36bd66117ae876ca82~mv2.jpeg/v1/fill/w_410,h_490,fp_0.62_0.47,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/WhatsApp%20Image%202026-01-18%20at%2016_24_16.jpeg"
                  alt="Atelier sensibilisation"
                  className="rounded-2xl shadow-2xl w-56 object-cover h-48"
                />
                <img
                  src="https://static.wixstatic.com/media/ce6ce7_85f1c1a30b21448f9f59276d697bb62f~mv2.jpg/v1/fill/w_440,h_630,fp_0.47_0.18,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/_ludivine-antonetti-maud-063.jpg"
                  alt="Maud formation"
                  className="rounded-2xl shadow-2xl w-56 object-cover h-64"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Banner */}
      <section className="bg-[#8C9D78] py-6">
        <div className="container-custom">
          <div className="grid grid-cols-3 gap-8 text-center text-white">
            <div data-testid="stat-ateliers">
              <p className="text-3xl md:text-4xl font-bold">17+</p>
              <p className="text-white/80 text-sm md:text-base mt-1">Ateliers maîtrisés</p>
            </div>
            <div data-testid="stat-personnes">
              <p className="text-3xl md:text-4xl font-bold">396+</p>
              <p className="text-white/80 text-sm md:text-base mt-1">Personnes sensibilisées</p>
            </div>
            <div data-testid="stat-formations">
              <p className="text-3xl md:text-4xl font-bold">10</p>
              <p className="text-white/80 text-sm md:text-base mt-1">Formations certifiées</p>
            </div>
          </div>
        </div>
      </section>

      {/* 4 Steps Process */}
      <section className="py-20 bg-[#F4F1EA]" data-testid="process-section">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-sm font-semibold text-[#C65D47] uppercase tracking-widest">Notre méthodologie</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1D3557] mt-4" style={{ fontFamily: "Fraunces, serif" }}>
              Un processus en 4 étapes
            </h2>
          </div>
          <div className="grid md:grid-cols-4 gap-6">
            {STEPS.map((step) => (
              <div key={step.num} className="relative group">
                <div className="bg-white rounded-2xl p-6 h-full border border-stone-200 hover:border-[#8C9D78]/50 hover:shadow-lg transition-all">
                  <div className="text-5xl font-bold text-[#8C9D78]/15 mb-4" style={{ fontFamily: "Fraunces, serif" }}>{step.num}</div>
                  <div className="w-12 h-12 rounded-xl bg-[#1D3557]/10 flex items-center justify-center mb-4">
                    <step.icon className="w-6 h-6 text-[#1D3557]" />
                  </div>
                  <h3 className="font-bold text-[#1D3557] text-lg mb-2">{step.title}</h3>
                  <p className="text-stone-600 text-sm leading-relaxed">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section id="categories" className="py-20" data-testid="categories-section">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-sm font-semibold text-[#8C9D78] uppercase tracking-widest">Nos publics</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1D3557] mt-4" style={{ fontFamily: "Fraunces, serif" }}>
              Des formations adaptées à chaque acteur
            </h2>
          </div>

          {/* Category Tabs */}
          <div className="flex flex-wrap gap-3 justify-center mb-12">
            {CATEGORIES.map((cat) => {
              const isActive = activeCategory === cat.id;
              const Icon = cat.icon;
              return (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`flex items-center gap-3 px-6 py-3.5 rounded-full text-base font-medium transition-all duration-300 ${
                    isActive
                      ? `bg-gradient-to-r ${cat.gradient} text-white shadow-lg scale-105`
                      : "bg-white text-stone-600 border border-stone-200 hover:border-stone-300 hover:shadow-md"
                  }`}
                  data-testid={`tab-${cat.id}`}
                >
                  <span className="text-xs font-bold opacity-60">{cat.num}</span>
                  <Icon className="w-5 h-5" />
                  {cat.title}
                </button>
              );
            })}
          </div>

          {/* Active Category Content */}
          {activeCat && (
            <div className="animate-in fade-in slide-in-from-bottom-4 duration-500" key={activeCat.id}>
              {/* Category Header */}
              <div className="bg-white rounded-3xl shadow-lg overflow-hidden mb-10">
                <div className={`bg-gradient-to-r ${activeCat.gradient} p-8 md:p-10`}>
                  <div className="flex items-start gap-6">
                    <div className="w-16 h-16 rounded-2xl bg-white/20 flex items-center justify-center flex-shrink-0">
                      <activeCat.icon className="w-8 h-8 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl md:text-3xl font-bold text-white mb-3" style={{ fontFamily: "Fraunces, serif" }}>
                        {activeCat.title}
                      </h3>
                      <p className="text-white/80 text-lg mb-2">{activeCat.subtitle}</p>
                      <p className="text-white/60">{activeCat.description}</p>
                    </div>
                  </div>
                </div>
                <div className="p-8 md:p-10">
                  <p className="text-stone-600 text-lg leading-relaxed mb-6">{activeCat.detail}</p>
                  {activeCat.passculture && (
                    <div className="flex items-center gap-3 bg-[#C65D47]/5 rounded-xl p-4 border border-[#C65D47]/20" data-testid="passculture-badge">
                      <BookOpen className="w-5 h-5 text-[#C65D47] flex-shrink-0" />
                      <p className="text-sm text-[#C65D47] font-medium">
                        Maud DUPUPET est référencée dans le dispositif <strong>Pass Culture</strong> — débloquez une enveloppe pour organiser des ateliers au sein de votre établissement.
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Workshops Grid */}
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="workshops-grid">
                {activeCat.workshops.map((workshop, i) => {
                  const selected = isWorkshopSelected(workshop);
                  return (
                    <div
                      key={i}
                      onClick={() => toggleWorkshop(workshop)}
                      className="group rounded-xl p-5 border-2 transition-all duration-300 cursor-pointer"
                      style={{
                        backgroundColor: selected ? activeCat.color : "white",
                        borderColor: selected ? activeCat.color : "#e7e5e4",
                        color: selected ? "white" : "inherit",
                      }}
                      onMouseEnter={(e) => {
                        if (!selected) {
                          e.currentTarget.style.borderColor = activeCat.color;
                          e.currentTarget.style.backgroundColor = activeCat.color + "12";
                          e.currentTarget.querySelector('[data-name]').style.color = activeCat.color;
                          e.currentTarget.querySelector('[data-chevron]').style.color = activeCat.color;
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!selected) {
                          e.currentTarget.style.borderColor = "#e7e5e4";
                          e.currentTarget.style.backgroundColor = "white";
                          e.currentTarget.querySelector('[data-name]').style.color = "#1D3557";
                          e.currentTarget.querySelector('[data-chevron]').style.color = "#d6d3d1";
                        }
                      }}
                      data-testid={`workshop-${i}`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <h4
                          data-name="true"
                          className="font-semibold pr-2 transition-colors"
                          style={{ color: selected ? "white" : "#1D3557" }}
                        >
                          {workshop.name}
                        </h4>
                        <div className="flex items-center gap-1 flex-shrink-0 mt-1">
                          {selected && (
                            <CheckCircle2 className="w-4 h-4" style={{ color: "white" }} />
                          )}
                          <ChevronRight
                            data-chevron="true"
                            className="w-4 h-4 transition-colors"
                            style={{ color: selected ? "white" : "#d6d3d1" }}
                          />
                        </div>
                      </div>
                      <div className="flex items-center gap-4 text-sm" style={{ color: selected ? "rgba(255,255,255,0.8)" : "#78716c" }}>
                        <div className="flex items-center gap-1.5">
                          <Clock className="w-3.5 h-3.5" />
                          {workshop.duration}
                        </div>
                        <div className="flex items-center gap-1.5">
                          <Users className="w-3.5 h-3.5" />
                          {workshop.participants}
                        </div>
                      </div>
                      <p className="text-xs mt-2" style={{ color: selected ? "rgba(255,255,255,0.6)" : "#a8a29e" }}>
                        {selected ? "Sélectionné — cliquez pour retirer" : "Cliquez pour sélectionner"}
                      </p>
                    </div>
                  );
                })}
              </div>

              {/* Selection summary */}
              {selectedWorkshops.filter(w => w.catId === activeCategory).length > 0 && (
                <div className="mt-6 p-4 rounded-xl border-2" style={{ borderColor: activeCat.color + "40", backgroundColor: activeCat.color + "08" }}>
                  <p className="text-sm font-semibold mb-1" style={{ color: activeCat.color }}>
                    {selectedWorkshops.filter(w => w.catId === activeCategory).length} formation(s) sélectionnée(s)
                  </p>
                  <p className="text-xs text-stone-500">
                    {selectedWorkshops.filter(w => w.catId === activeCategory).map(w => w.name).join(" · ")}
                  </p>
                </div>
              )}

              {/* CTA for category */}
              <div className="text-center mt-8">
                <Button onClick={openQuoteWithSelection} className="rounded-full px-8 h-12 text-white" style={{ backgroundColor: activeCat.color }} data-testid="category-cta-btn">
                  {selectedWorkshops.filter(w => w.catId === activeCategory).length > 0
                    ? `Demander un devis (${selectedWorkshops.filter(w => w.catId === activeCategory).length} formation${selectedWorkshops.filter(w => w.catId === activeCategory).length > 1 ? "s" : ""})`
                    : "Demander un devis pour cette offre"
                  }
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Maud Section - Animatrice */}
      <section className="py-20 bg-[#1D3557]" data-testid="maud-section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="flex justify-center">
              <div className="relative">
                <div className="absolute -inset-4 bg-[#8C9D78]/20 rounded-3xl blur-2xl" />
                <img
                  src="https://static.wixstatic.com/media/ce6ce7_46ec30106ce34291b7a2bcead49b1824~mv2.png/v1/fill/w_479,h_709,fp_0.49_0.13,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/photo%20maud%20site.png"
                  alt="Maud Dupupet"
                  className="relative rounded-2xl shadow-2xl max-w-xs md:max-w-sm"
                />
                <div className="absolute -bottom-4 -right-4 bg-[#8C9D78] text-white rounded-2xl p-4 shadow-xl">
                  <p className="text-sm font-bold">396+ personnes</p>
                  <p className="text-xs text-white/70">sensibilisées</p>
                </div>
              </div>
            </div>
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#8C9D78]/20 text-[#8C9D78]">
                <GraduationCap className="w-4 h-4" />
                <span className="text-sm font-medium">Votre formatrice</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-white" style={{ fontFamily: "Fraunces, serif" }}>
                Maud Dupupet
              </h2>
              <p className="text-white/60 text-lg">Animatrice & Facilitatrice — Défis du XXIème siècle</p>
              
              <div className="flex flex-wrap gap-3" data-testid="maud-roles">
                <Badge className="bg-[#C65D47]/20 text-[#C65D47] border-0 px-4 py-1.5 text-sm">Enseignante en DESIGN DURABLE</Badge>
                <Badge className="bg-[#8C9D78]/20 text-[#8C9D78] border-0 px-4 py-1.5 text-sm">Enseignante en PROCESSUS DESIGN</Badge>
              </div>

              <p className="text-white/70 leading-relaxed">
                Je conçois des expériences innovantes, immersives et transformantes pour accompagner
                vos réflexions stratégiques. Je facilite grâce à des techniques d'animation pour permettre
                aux collaborateurs de travailler ensemble, de mieux percevoir les objectifs communs.
              </p>
              <p className="text-white/70 leading-relaxed">
                J'ai participé à <strong className="text-white">17 ateliers</strong> différents et me suis formée à <strong className="text-white">10 formations</strong> pour
                devenir animatrice-facilitatrice certifiée. J'interviens exclusivement en <strong className="text-white">Corse</strong>.
              </p>
              <div className="flex flex-wrap gap-2 pt-2">
                {["Climat", "Biodiversité", "Économie circulaire", "RSE", "Mobilité", "Alimentation", "Numérique", "Textile"].map((tag) => (
                  <span key={tag} className="text-xs px-3 py-1.5 rounded-full bg-white/10 text-white/70 border border-white/10">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pourquoi Section */}
      <section className="py-20 bg-[#F4F1EA]" data-testid="pourquoi-section">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <span className="text-sm font-semibold text-[#C65D47] uppercase tracking-widest">Pourquoi ?</span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#1D3557] mt-4" style={{ fontFamily: "Fraunces, serif" }}>
                Prise de conscience et déclic écologique
              </h2>
            </div>
            <div className="bg-white rounded-3xl p-8 md:p-12 shadow-lg">
              <div className="space-y-6 text-stone-600 leading-relaxed">
                <p className="text-lg">
                  L'histoire de cette offre démarre le <strong className="text-[#1D3557]">18 août 2022</strong>, ce jour-là une tempête d'une violence
                  inédite s'est abattue sur la Corse, avec des vents violents atteignant des rafales à
                  <strong className="text-[#C65D47]"> 197 km/h sur Calvi</strong> et des dégâts considérables (matériels et humains).
                </p>
                <p>
                  Cet événement climatique extrême a été pour moi l'élément déclencheur pour passer à l'action.
                </p>
                <p>
                  Depuis cette date, j'ai découvert les ateliers de sensibilisation, une méthode d'apprentissage
                  ludique basée sur l'intelligence collective et conviviale. Il existe des ateliers de sensibilisation
                  sur des sujets divers et variés.
                </p>
                <div className="bg-[#1D3557]/5 rounded-xl p-6 border-l-4 border-[#1D3557]">
                  <p className="text-sm text-stone-500 italic">
                    J'ai développé cette offre pour vous éviter cette coûteuse et douloureuse prise de conscience.
                    Cette offre est réservée à toutes les entreprises insulaires et adaptée aux enjeux de la transition
                    écologique particulièrement marquants en Corse.
                  </p>
                  <p className="text-xs text-stone-400 mt-3">
                    La Corse connaît des hausses de températures moyennes annuelles comprises entre 1,4°C et 1,6°C
                    par rapport aux niveaux préindustriels.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#8C9D78]">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" style={{ fontFamily: "Fraunces, serif" }}>
            Prêt à mobiliser vos équipes ?
          </h2>
          <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto">
            Contactez-nous pour créer un programme de formation adapté à vos besoins
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button onClick={() => setDialogOpen(true)} className="bg-white text-[#8C9D78] hover:bg-stone-100 rounded-full px-10 h-14 text-lg font-semibold">
              Demander un devis
            </Button>
            <Button asChild variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-[#8C9D78] rounded-full px-10 h-14 text-lg">
              <a href="tel:+33751131092">07 51 13 10 92</a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FormationsPage;
